/* whero-shipping-schedule.js — single source of truth for shipping ETA.

   Exposes window.WheroShipping.computeDelivery(), used by:
     - assets/whero-status-pill.js (header location capsule, >60mi mode)
     - snippets/whero-shipping-eta.liquid (shipping band + bundle tray)

   Schedule (America/Los_Angeles):
     Thu 9pm → Mon 9am  : Tuesday delivery
     Mon 9am → Tue 9am  : Wednesday
     Tue 9am → Wed 9am  : Thursday
     Wed 9am → Thu 9pm  : Friday

   Blackouts come from the #whero-shipping-blackouts JSON data island,
   populated from the shipping_blackout metaobject. If the base delivery
   date is in the blackout list, advances to the next shipping weekday
   (Tue/Wed/Thu/Fri) that isn't blacked out.
*/
(function () {
  'use strict';

  var LA_TZ = 'America/Los_Angeles';
  var MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  var SHIPPING_WEEKDAYS = {
    Tuesday: true, Wednesday: true, Thursday: true, Friday: true
  };

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function ordinalSuffix(n) {
    var v = n % 100;
    if (v >= 11 && v <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  function monthNameToNum(name) {
    var i = MONTHS.indexOf(name);
    return i === -1 ? 1 : i + 1;
  }

  // Returns LA-local components for a given Date.
  function laDateInfo(date) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: LA_TZ,
      year: 'numeric', month: 'long', day: 'numeric',
      weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false
    });
    var parts = fmt.formatToParts(date);
    var bag = {};
    for (var i = 0; i < parts.length; i++) bag[parts[i].type] = parts[i].value;
    var year = parseInt(bag.year, 10);
    var monthName = bag.month;
    var monthNum = monthNameToNum(monthName);
    var day = parseInt(bag.day, 10);
    var weekday = bag.weekday;
    return {
      year: year,
      month: monthName,
      monthNum: monthNum,
      day: day,
      weekday: weekday,
      weekdayShort: weekday.toLowerCase().slice(0, 3),
      iso: year + '-' + pad2(monthNum) + '-' + pad2(day),
      hour: parseInt(bag.hour, 10) % 24,
      minute: parseInt(bag.minute, 10)
    };
  }

  // Anchor at noon UTC of today's LA date, then add N days. Stable across DST —
  // a noon-UTC anchor stays solidly inside the same LA-local day after ±1h shift.
  function addDaysFromTodayLA(n) {
    var today = laDateInfo(new Date());
    var anchor = new Date(Date.UTC(today.year, today.monthNum - 1, today.day, 19, 0, 0));
    anchor.setUTCDate(anchor.getUTCDate() + n);
    return laDateInfo(anchor);
  }

  // Maps "now" (LA-local weekday + minutes) to the base delivery day name.
  function scheduledDayName(weekdayShort, mins) {
    var NINE_AM = 540;
    var NINE_PM = 1260;
    if (weekdayShort === 'thu' && mins >= NINE_PM) return 'Tuesday';
    if (weekdayShort === 'fri' || weekdayShort === 'sat' || weekdayShort === 'sun') return 'Tuesday';
    if (weekdayShort === 'mon' && mins <  NINE_AM)  return 'Tuesday';
    if (weekdayShort === 'mon' && mins >= NINE_AM)  return 'Wednesday';
    if (weekdayShort === 'tue' && mins <  NINE_AM)  return 'Wednesday';
    if (weekdayShort === 'tue' && mins >= NINE_AM)  return 'Thursday';
    if (weekdayShort === 'wed' && mins <  NINE_AM)  return 'Thursday';
    if (weekdayShort === 'wed' && mins >= NINE_AM)  return 'Friday';
    if (weekdayShort === 'thu' && mins <  NINE_PM)  return 'Friday';
    return 'Tuesday';
  }

  function readBlackouts() {
    var el = document.getElementById('whero-shipping-blackouts');
    if (!el) return {};
    try {
      var parsed = JSON.parse(el.textContent || '[]');
      var map = {};
      for (var i = 0; i < parsed.length; i++) {
        var b = parsed[i];
        if (b && b.date) map[b.date] = b.label || '';
      }
      return map;
    } catch (e) {
      return {};
    }
  }

  function formatLabel(info) {
    return info.weekday + ', ' + info.month + ' ' + info.day + ordinalSuffix(info.day);
  }

  function computeDelivery() {
    var nowInfo = laDateInfo(new Date());
    var baseDay = scheduledDayName(nowInfo.weekdayShort, nowInfo.hour * 60 + nowInfo.minute);

    // Find first date >= today whose weekday name matches the base.
    var idx = -1;
    var target = null;
    for (var i = 0; i < 7; i++) {
      var d = addDaysFromTodayLA(i);
      if (d.weekday === baseDay) { target = d; idx = i; break; }
    }
    if (!target) { target = addDaysFromTodayLA(7); idx = 7; }

    var blackouts = readBlackouts();
    var blackoutLabel = '';

    // If the base delivery date is blacked out (or somehow lands on a
    // non-shipping weekday), advance one day at a time to the next
    // shipping weekday that isn't blacked out. Cap at 21 days for safety.
    var safety = 0;
    while (safety < 21 && (blackouts.hasOwnProperty(target.iso) || !SHIPPING_WEEKDAYS[target.weekday])) {
      if (blackouts.hasOwnProperty(target.iso) && !blackoutLabel) {
        blackoutLabel = blackouts[target.iso];
      }
      idx++;
      target = addDaysFromTodayLA(idx);
      safety++;
    }

    return {
      dayName: target.weekday,
      monthName: target.month,
      dayOfMonth: target.day,
      ordinal: ordinalSuffix(target.day),
      // Canonical display string: "Tuesday, May 19th"
      dateLabel: formatLabel(target),
      iso: target.iso,
      skippedBlackoutLabel: blackoutLabel
    };
  }

  window.WheroShipping = {
    computeDelivery: computeDelivery,
    _laDateInfo: laDateInfo,
    _readBlackouts: readBlackouts,
    _scheduledDayName: scheduledDayName
  };
})();
